// react
import { ChangeEvent, Dispatch, MouseEvent, SetStateAction, useContext, useState } from 'react';

// react-router-dom
import { useLocation } from 'react-router-dom';

// types
import { Entity, ErrorCause, Feedback } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner, ContextUser, ContextUserEntity } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// mui
import { Alert, AlertTitle, Grid, SelectChangeEvent, Typography } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';
import { Select } from '../../fields/Select/Select';

// props
export interface FormAddIncidentProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormAddIncident = ({ onClose, onSuccess, setIsLoading }: FormAddIncidentProps) => {
	// hooks
	const location = useLocation();
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const { user } = useContext(ContextUser);
	const { userEntity } = useContext(ContextUserEntity);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// vars
	const idForm = 'form-add-incident';
	let defaultModule = '';
	const path = location.pathname.toLowerCase();

	if (path.includes('/overview')) {
		defaultModule = 'overview';
	} else if (path.includes('/financing')) {
		defaultModule = 'financing';
	} else if (path.includes('/accounts')) {
		defaultModule = 'accounts';
	} else if (path.includes('/transactions')) {
		defaultModule = 'transactions';
	} else if (path.includes('/activities')) {
		defaultModule = 'activities';
	} else if (path.includes('/contracts')) {
		defaultModule = 'contracts';
	} else if (path.includes('/suppliers')) {
		defaultModule = 'suppliers';
	} else if (path.includes('/entities')) {
		defaultModule = 'entities';
	} else if (path.includes('/beneficiaries')) {
		defaultModule = 'beneficiaries';
	} else if (path.includes('/reports')) {
		defaultModule = 'reports';
	} else if (path.includes('/settings')) {
		defaultModule = 'settings';
	} else if (path.includes('/project/')) {
		defaultModule = 'overview'; // set to 'overview' if project index page is detected
	} else {
		defaultModule = 'other'; // Default to 'other' if no specific module is detected
	}

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [values, setValues] = useState({
		title: '',
		type: 'ISSUE',
		module: defaultModule,
		description: '',
		firstName: user?.firstName || '',
		lastName: user?.lastName || '',
		email: user?.email || '',
		projectId: project?.idWbg || '',
		projectName: project?.name || '',
		projectCountry: project?.country.name || '',
	});

	// types
	const optionsIncidentTypes = [
		{ value: 'ISSUE', label: t('reportAnIssue') },
		{ value: 'ENHANCEMENT', label: t('suggestAnEnhancement') },
		{ value: 'OTHER', label: t('other') },
	];

	// Module tabs for selection
	const optionsModules = [
		{ value: 'overview', label: t('overview') },
		{ value: 'financing', label: t('financing') },
		{ value: 'accounts', label: t('accounts') },
		{ value: 'transactions', label: t('transactions') },
		{ value: 'activities', label: t('activities') },
		{ value: 'contracts', label: t('contracts') },
		{ value: 'suppliers', label: t('suppliers') },
		{ value: 'entities', label: t('entities') },
		{ value: 'beneficiaries', label: t('beneficiaries') },
		{ value: 'fundflow', label: t('fundFlow') },
		{ value: 'reports', label: t('reports') },
		{ value: 'settings', label: t('settings') },
		{ value: 'other', label: t('other') },
	];

	// handlers
	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		try {
			// clear error
			setFeedback(null);

			// set loader
			setIsLoading(true);

			const fetchAddIncident = await fetch(`${process.env.REACT_APP_API_URL}/incidents`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify({
					description: values.description,
					email: values.email,
					firstName: values.firstName,
					lastName: values.lastName,
					module: values.module,
					projectCountry: values.projectCountry,
					projectId: values.projectId,
					projectName: values.projectName,
					title: values.title,
					type: values.type,
					entityName: (userEntity as Entity)?.name,
					entityId: (userEntity as Entity)?.id,
				}),
			});

			const resAddIncident = await fetchAddIncident.json();

			if (resAddIncident.error) {
				throw new Error(resAddIncident.error.message, {
					cause: {
						id: resAddIncident.error.id,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.incidentCreated', {
					ticketNumber: resAddIncident.data.ticketNumber,
				}),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew: Feedback = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'CRM_GENERATE_TOKEN_ERROR') {
					feedbackNew.alert.children = t('error.crmGenerateAccessTokenFailed');
				}

				if (cause.id === 'CRM_CREATE_CONTACT_ERROR') {
					feedbackNew.alert.children = t('error.crmCreateContactFailed');
				}

				if (cause.id === 'CRM_CREATE_INCIDENT_ERROR') {
					feedbackNew.alert.children = t('error.crmCreateIncidentFailed');
				}

				if (cause.id === 'CRM_CREATE_ANNOTATION_ERROR') {
					feedbackNew.alert.children = t('error.crmCreateAnnotationFailed');
				}
			}

			// set feedback
			setFeedback(feedbackNew);
		} finally {
			// set loader
			setIsLoading(false);
		}
	};

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	return (
		<form id={idForm} name="form-add-incident" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Input
						focused
						id={`${idForm}-title`}
						label={t('title')}
						name="title"
						required={true}
						onChange={handleChangeValue}
						value={values.title}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Select
						id={`${idForm}-incident-type`}
						label={t('incidentType')}
						name="type"
						options={optionsIncidentTypes}
						required={true}
						onChange={handleChangeValue}
						value={values.type}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Select
						id={`${idForm}-module`}
						label={t('Module')}
						name="module"
						options={optionsModules}
						required={true}
						onChange={handleChangeValue}
						value={values.module}
					/>
				</Grid>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-description`}
						label={t('description')}
						name="description"
						required={true}
						multiline={true}
						rows={6}
						onChange={handleChangeValue}
						value={values.description}
					/>
				</Grid>
				<Grid item xs={12}>
					<Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
						{t('additionalInformationIncluded')}
					</Typography>
				</Grid>

				<Grid item xs={6} md={6}>
					<Typography variant="h3">{t('firstName')}</Typography>
					<Typography>{values?.firstName ? values?.firstName : t('na')}</Typography>
				</Grid>
				<Grid item xs={6} md={6}>
					<Typography variant="h3">{t('lastName')}</Typography>
					<Typography>{values?.lastName ? values?.lastName : t('na')}</Typography>
				</Grid>
				<Grid item xs={6} md={6}>
					<Typography variant="h3">{t('email')}</Typography>
					<Typography>{values?.email ? values?.email : t('na')}</Typography>
				</Grid>
				<Grid item xs={6} md={6}>
					<Typography variant="h3">{t('projectId')}</Typography>
					<Typography>{values?.projectId ? values?.projectId : t('na')}</Typography>
				</Grid>
				<Grid item xs={6} md={6}>
					<Typography variant="h3">{t('projectName')}</Typography>
					<Typography>{values?.projectName ? values?.projectName : t('na')}</Typography>
				</Grid>

				<Grid item xs={6} md={6}>
					<Typography variant="h3">{t('country')}</Typography>
					<Typography>{values?.projectCountry ? values?.projectCountry : t('na')}</Typography>
				</Grid>
				<Grid item xs={6} md={6}>
					<Typography variant="h3">{t('entity')}</Typography>
					<Typography>{userEntity ? userEntity?.name : t('na')}</Typography>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
